'use strict';
import { getFocusableElements, setTabindex } from '../utils/_domFunctions';

class mReveal extends HTMLDivElement {
    constructor() {
        super();
        this.elemTrigger = null;
        this.elemContent = null;
        this.isContentOpen = false;
        this.inTransition = false;

        // bind this
        this.toggleContent = this.toggleContent.bind(this);
        this.onContentTransitionEnd = this.onContentTransitionEnd.bind(this);
    }

    connectedCallback() {
        // setup elemTrigger
        this.elemTrigger = this.querySelector('[data-trigger]') || this; // if no specific trigger provided use the m-reveal-element itself as trigger
        if (this.elemTrigger.tagName === 'A' || this.elemTrigger.tagName === 'BUTTON') {
            this.elemTrigger.addEventListener('click', () => { this.toggleContent(); });
        } else {
            // not togglable
            this.elemTrigger.addEventListener('click', () => { this.toggleContent(true); });
            this.elemTrigger.addEventListener('focus', () => { this.toggleContent(true); });
        }

        // setup elemContent
        this.elemContent = this.querySelector('.content');
        this.elemContent.addEventListener('transitionstart', this.onContentTransitionStart);
        this.elemContent.addEventListener('transitionend', this.onContentTransitionEnd);

        this.updateVisibility();

        if ('gtmClickEvent' in this.elemTrigger.dataset) {
            this.gtmClickEvent = this.elemTrigger.dataset.gtmClickEvent;
        }

        // if the trigger has data-reveal-if-prefilled + it's an input + it's prefilled, toggle the content (used in back in stock)
        if ('revealIfPrefilled' in this.elemTrigger.dataset && this.elemTrigger.tagName === 'INPUT' && this.elemTrigger.value !== '') {
            this.toggleContent(true);
        }
    }

    /**
     * Change content element visibility state
     * @param {boolean} [state] Force visibility state
     */
    toggleContent(state) {
        if (state && this.gtmClickEvent) {
            // Remove gtm click event when content revealed (in setTimeout to wait the end of the event chain)
            setTimeout(() => {
                delete this.elemTrigger.dataset.gtmClickEvent;
            });
        } else if (this.gtmClickEvent) {
            setTimeout(() => {
                this.elemTrigger.dataset.gtmClickEvent = this.gtmClickEvent;
            });
        }

        if (this.isContentOpen === state) return;
        this.isContentOpen = (state === undefined ? !this.isContentOpen : state);
        // trigger the transition
        this.elemContent.style.maxHeight = this.elemContent.scrollHeight + 'px';
        if (!this.isContentOpen) {
            // need setTimeout to trigger smooth transition between scrollHeight -> 0
            setTimeout(() => { this.elemContent.style.maxHeight = '0'; }, 0);
        }
    }

    /**
     * Update aria attributes and focus state according to component state
    */
    updateVisibility() {
        const visible = !this.inTransition && this.isContentOpen;
        this.elemTrigger.setAttribute('aria-expanded', visible);
        this.elemContent.setAttribute('aria-hidden', !visible);
        setTabindex(getFocusableElements(this.elemContent), visible ? 0 : -1);
    }

    /**
     * "transitionstart" callback for the content element
     * @param {TransitionEvent} e Transition start event
     */
    onContentTransitionStart(e) {
        if (e.target === this.elemContent && e.propertyName === 'max-height') {
            this.inTransition = true;
            this.updateVisibility();
        }
    }

    /**
     * "transitionend" callback for the content element
     * @param {TransitionEvent} e Transition end event
     */
    onContentTransitionEnd(e) {
        if (e.target === this.elemContent && e.propertyName === 'max-height') {
            this.inTransition = false;
            this.elemContent.style.maxHeight = null;
            this.updateVisibility();
        }
    }
}

customElements.define('m-reveal', mReveal, {
    extends: 'div'
});
